'use client';

import { ContentApiDepths } from '@/constants/content-api-depths';
import { QueryKeys } from '@/constants/query-keys';
import { moviesApiService } from '@/services/(frontend)/movies/movie-api.service';
import { Movie, MovieGlobal } from '@cinemark/content-api-client';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';

type FeaturedCarouselMovies = {
  data: Movie[];
  isLoading: boolean;
};

export const useFeaturedCarouselMovies = (): FeaturedCarouselMovies => {
  const updateQueryStaleTime = useDynamicStaleTime();

  const movieGlobalQuery = useQuery<MovieGlobal | null>({
    queryFn: async ({ signal }) => {
      const response = await moviesApiService.getMovieGlobal(signal, ContentApiDepths.featuredMovies);

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.global.movieGlobal(ContentApiDepths.featuredMovies), response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.global.movieGlobal(ContentApiDepths.featuredMovies),
  });

  const featuredMovies = movieGlobalQuery.data?.featuredMovies || [];
  const featuredMovieIds = featuredMovies.flatMap(({ id }) => id ? [id] : []);
  const movieQueries = useQueries({
    queries: featuredMovieIds.map(payloadId => ({
      queryFn: async ({ signal }): Promise<Movie | null> => {
        const response = await moviesApiService.getMovie(payloadId, signal, ContentApiDepths.featuredMovie);

        if (response.headers) {
          updateQueryStaleTime(QueryKeys.global.movie(payloadId, ContentApiDepths.featuredMovie), response.headers);
        }

        return response.data;
      },
      queryKey: QueryKeys.global.movie(payloadId, ContentApiDepths.featuredMovie),
    })),
  });

  return {
    data: movieQueries.flatMap(query => query.data ? [query.data] : []),
    isLoading: movieGlobalQuery.isLoading || movieQueries.some(query => query.isLoading),
  };
};
